import {
  KeyboardArrowDown,
  OpenInNew,
  ShoppingCartOutlined,
  Update as UpdateIcon,
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import {
  Box,
  Grid,
  IconButton,
  Input,
  Menu,
  MenuItem,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material';
import { BlocksActionPanel } from 'components/BlocksList/BlocksActionPanel/BlocksActionPanel';
import { ChangeLog } from 'components/BlocksList/ChangeLog';
import { useBlocksPage } from 'components/BlocksList/hooks/useBlocksPage';
import { Button } from 'components/common/Button/Button';
import { TeamSelector } from 'components/common/TeamSelector/TeamSelector';
import { ExportToExcelButton } from 'components/OrderForm/Buttons/ExportToExcelButton';
import { Spinner } from 'components/Loading/Spinner/Spinner';
import { DefaultModal } from 'components/Modals/DefaultModal';
import { useSlideInfoForPathologyMapStyles } from 'components/PathologyMap/constants/styles';
import { OrdersSortTeamPage } from 'components/UserProfile/components/OrdersSortTeamPage';
import { OrderInfo } from 'components/UserProfile/OrdersCard/OrderInfo';
import { useTitle } from 'components/utilities/hooks/useTitle';
import { useBlocksPageV2Styles } from 'components/BlocksList/styles';
import {
  getDataPath,
  getDropdownItemsForBlocksPage,
} from 'components/BlocksList/utilities';
import {
  autoGroupColumnDef,
  columnDefs,
  defaultColDef,
} from 'components/BlocksList/constants';
import React from 'react';
import { CustomDropdown } from 'components/TeamOverview/components/ManageDataCard/CustomDropdown';
import { handleOpenAsGroup } from 'components/OrderSlidesList/utilities';
import { useLocation } from 'react-router';
import { TextWithLimitedWidth } from 'components/SlideGroups/components/TextWithLimitedWidth';
import { useIsDemoUser } from 'components/utilities/hooks/useIsDemoUser';

export const BlocksPageV2 = () => {
  useTitle('HistoWiz - Blocks List');
  const { classes } = useBlocksPageV2Styles();
  const { classes: classesFromPathologyMap } =
    useSlideInfoForPathologyMapStyles();
  const location = useLocation();
  const isDemoUser = useIsDemoUser();

  const {
    selectedTeam,
    teamInfo,
    setSelectedTeam,
    teams,
    tableTitle,
    selectedRows,
    handleAddToQueue,
    onQuickFilterText,
    handleSwitchChange,
    filteredRowData,
    checkRowSelectable,
    onGridReady,
    onRowSelected,
    quickFilterText,
    isOnlyWithSlides,
    processingQueue,
    setProcessingQueue,
    handleCreateNewOrder,
    activeTab,
    setActiveTab,
    selectedSamples,
    setSelectedSamples,
    selectedBlocksType,
    setSelectedBlocksType,
    blocksModalOpen,
    handleBlockModalClose,
    orders,
    isOrdersLoading,
    handleBlockModalOpen,
    sort,
    setSort,
    handleAddToNewOrder,
    selectedSlides,
    handleFirstDataRendered,
    onRowGroupOpened,
    resetSelectedRows,
    changeLogActive,
    setChangeLogActive,
    teamUUID,
    projects,
    isLoading,
    handleMenuClick,
    menuAnchor,
    handleMenuClose,
    handleOpenChangeLog,
    handleOpenPending,
    teamBlocksCount,
    handleExportToExcel,
  } = useBlocksPage();

  const isGroupOpenByDefault = (params) => {
    const hash = new URLSearchParams(location.hash);
    const projectName = hash.get('#project');

    return params.rowNode.key === projectName;
  };

  const renderOrdersList = () =>
    orders.length ? (
      orders.map((order) => (
        <OrderInfo
          onRowClick={() => handleAddToNewOrder(order.uuid)}
          key={order.uuid}
          order={order}
          classes={classesFromPathologyMap}
        />
      ))
    ) : (
      <Box height="100%" display="flex" alignItems="center">
        <Typography>No such orders</Typography>
      </Box>
    );

  return (
    <>
      <Box className={classes.pageHeader}>
        <Typography className={classes.title}>Block Manager</Typography>
        <Box className={classes.separator} />
        <Box className={classes.title}>
          BLOCKS IN STORAGE:{' '}
          {isLoading ? (
            <Skeleton
              variant="text"
              width={20}
              className={classes.titleSkeleton}
            />
          ) : (
            filteredRowData?.length || 0
          )}
        </Box>
        <Box className={classes.separator} />
        <Box className={classes.teamSelect}>
          {selectedTeam ? (
            <Typography className={classes.title}>{teamInfo}</Typography>
          ) : (
            <Skeleton variant="text" width={200} />
          )}
          <TeamSelector
            selectedTeam={selectedTeam}
            setSelectedTeam={setSelectedTeam}
            teams={teams}
            teamBlocksCount={teamBlocksCount}
            classes={classes}
          />
        </Box>
      </Box>
      {changeLogActive ? (
        <Box mt={2}>
          <ChangeLog
            type={changeLogActive}
            teamUUID={teamUUID}
            handleClose={() => setChangeLogActive(null)}
            projects={projects}
          />
        </Box>
      ) : (
        <Grid container className={classes.container}>
          <Grid item xs={8} className={classes.tableWrapper}>
            <Box>
              <div className={`ag-theme-balham ${classes.table}`}>
                <Box className={classes.tableHeader}>
                  <Box className={classes.tableTitleContainer}>
                    <TextWithLimitedWidth
                      containerClass={classes.tableTitleWrapper}
                      textClass={classes.tableTitle}
                      value={tableTitle}
                      tooltipText={tableTitle}
                      tooltipPlacement="top"
                    />
                  </Box>

                  <Box className={classes.tableHeaderContainer}>
                    {selectedSlides.length && selectedRows.length ? (
                      <Button
                        className={classes.viewSlidesButton}
                        variant="outlined"
                        color="blue"
                        endIcon={<OpenInNew />}
                        onClick={() => handleOpenAsGroup(selectedSlides)}
                      >
                        <Typography className={classes.associatedText}>
                          View {selectedSlides.length} associated slides
                        </Typography>
                      </Button>
                    ) : null}
                    <CustomDropdown
                      width="15rem"
                      setValue={setSelectedBlocksType}
                      value={selectedBlocksType}
                      getDropdownItems={getDropdownItemsForBlocksPage}
                    />
                    <Button
                      variant="contained"
                      color="blue"
                      startIcon={<ShoppingCartOutlined />}
                      disabled={!selectedRows?.length || isDemoUser}
                      onClick={handleAddToQueue}
                      className={classes.addSamplesBtn}
                    >
                      Add{' '}
                      <Box className={classes.processCounter}>
                        {selectedRows?.length}
                      </Box>{' '}
                      samples to queue
                    </Button>
                    <Box>
                      <IconButton
                        className={classes.changelog}
                        onClick={handleMenuClick}
                      >
                        <UpdateIcon className={classes.changeIcon} />
                        <KeyboardArrowDown />
                      </IconButton>
                      <Menu
                        open={!!menuAnchor}
                        anchorEl={menuAnchor}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        className={classes.menu}
                      >
                        <MenuItem
                          className={classes.menuItem}
                          onClick={handleOpenPending}
                        >
                          Pending requests
                        </MenuItem>
                        <MenuItem
                          className={classes.menuItem}
                          onClick={handleOpenChangeLog}
                        >
                          Changelog
                        </MenuItem>
                      </Menu>
                    </Box>
                    <ExportToExcelButton
                      isLoading={isLoading || !filteredRowData?.length}
                      onClick={handleExportToExcel}
                      styles={classes.exportButton}
                    />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Input
                    onChange={onQuickFilterText}
                    value={quickFilterText}
                    className={classes.searchInput}
                    placeholder="Enter text to filter..."
                    disableUnderline
                  />
                  <Box className={classes.switch}>
                    <Switch
                      size="small"
                      checked={isOnlyWithSlides}
                      onChange={handleSwitchChange}
                    />
                    Show blocks with slides only
                  </Box>
                </Box>
                <AgGridReact
                  rowData={filteredRowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  autoGroupColumnDef={autoGroupColumnDef}
                  isRowSelectable={checkRowSelectable}
                  isGroupOpenByDefault={isGroupOpenByDefault}
                  enableBrowserTooltips
                  treeData
                  animateRows
                  quickFilterText={quickFilterText}
                  onGridReady={onGridReady}
                  onRowSelected={onRowSelected}
                  onFirstDataRendered={handleFirstDataRendered}
                  groupDefaultExpanded={0}
                  rowSelection="multiple"
                  groupSelectsChildren
                  getDataPath={getDataPath}
                  getRowId={({ data }) => data?.uuid}
                  overlayNoRowsTemplate="<span>No data</span>"
                  onRowGroupOpened={onRowGroupOpened}
                />
              </div>
            </Box>
          </Grid>
          <BlocksActionPanel
            processingQueue={processingQueue}
            setProcessingQueue={setProcessingQueue}
            openBlocksModal={handleBlockModalOpen}
            handleCreateOrder={handleCreateNewOrder}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            selectedSamples={selectedSamples}
            selectedRows={selectedRows}
            setSelectedSamples={setSelectedSamples}
            resetSelectedRows={resetSelectedRows}
            selectedSlides={selectedSlides}
          />
        </Grid>
      )}
      <DefaultModal open={blocksModalOpen} onClose={handleBlockModalClose}>
        <Box className={classes.formWrapper}>
          <OrdersSortTeamPage sort={sort} setSort={setSort} />
          {isOrdersLoading && (
            <Box className={classes.spinnerContainer}>
              <Spinner />
            </Box>
          )}
          {!isOrdersLoading && orders.length
            ? orders.map((order) => (
              <OrderInfo
                onRowClick={() => handleAddToNewOrder(order.uuid)}
                key={order.uuid}
                order={order}
                classes={classesFromPathologyMap}
              />
            ))
            : renderOrdersList()}
        </Box>
      </DefaultModal>
    </>
  );
};
